import { useEffect, useRef, useState } from "react";

interface VisualData {
  data: any[];
  metadata: { columns: string[] };
}

interface UseVisualDataParams {
  activeDataflowId: string | null;
  originalData: any[] | null;
}

export function useVisualData({
  activeDataflowId,
  originalData,
}: UseVisualDataParams): VisualData | null {
  const [visualData, setVisualData] = useState<VisualData | null>(null);

  const prevDataRef = useRef<{
    activeDataflowId: string | null;
    originalDataLength: number;
    processedDataLength: number;
    processedDataColumns: string;
  }>({
    activeDataflowId: null,
    originalDataLength: 0,
    processedDataLength: 0,
    processedDataColumns: "",
  });

  useEffect(() => {
    if (!activeDataflowId) {
      setVisualData(null);
      prevDataRef.current = {
        activeDataflowId: null,
        originalDataLength: 0,
        processedDataLength: 0,
        processedDataColumns: "",
      };
      return;
    }

    if (!originalData || originalData.length === 0) {
      setVisualData(null);
      prevDataRef.current = {
        activeDataflowId: null,
        originalDataLength: 0,
        processedDataLength: 0,
        processedDataColumns: "",
      };
      return;
    }

    const currentOriginalLength = originalData?.length || 0;
    const hasChanged =
      prevDataRef.current.activeDataflowId !== activeDataflowId ||
      prevDataRef.current.originalDataLength !== currentOriginalLength;

    if (!hasChanged) {
      return;
    }

    prevDataRef.current = {
      activeDataflowId,
      originalDataLength: currentOriginalLength,
      processedDataLength: 0,
      processedDataColumns: "",
    };

    const columns = originalData.length > 0 ? Object.keys(originalData[0]) : [];
    setVisualData({
      data: originalData,
      metadata: { columns },
    });
  }, [activeDataflowId, originalData]);

  return visualData;
}
